import styles from "./style.module.scss";

import DynamicForm, { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import SalesByProductFormConfig from "./form";
import { Button, Col, Row, Table } from "reactstrap";
import { SalesByProductFormDataType } from "@/formTypes/SalesByProductFormDataType";
import moment from "moment";
import AddProductToDocumentOffCanvas from "@components/layout/Offcanvas/AddProductToDocumentOffCanvas";
import { OpenAddProductPubSub } from "@utils/pubsubs/PubSub";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { showAlert } from "@utils/Alert";
import { ClientApi } from "@/api/ClientApi";
import { InvoiceProductApi, InvoiceProductDataType } from "@/api/InvoiceProductApi";
import LoadingScreen from "@components/content/LoadingScreen";
import BarGraph from "@components/content/BarGraph";
import { Link } from "react-router-dom";
import { toNumberFormat } from "@utils/Utils";
import { formatCant, roundCant } from "@utils/Cant";

type SalesByProductFormProps = {
}

type SalesByMonthRow = {
  month: string;     // "YYYY-MM"
  totalCant: number; // suma de 'cant'
  count: number;     // cantidad de registros involucrados
};

const SalesByProductForm = forwardRef<DynamicFormRef, SalesByProductFormProps>((props, ref) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGraphGenerated, setGraphGenerated] = useState<boolean>(false);
  const [product, setProduct] = useState<EDocumentProductDataType>()
  const [clients, setClients] = useState<Record<string, string>>()
  const [products, setProducts] = useState<InvoiceProductDataType[]>([])
  const [graphData, setGraphData] = useState<SalesByMonthRow[]>([])

  useEffect(() => {
    const endDate = moment().format("YYYY-MM-DD");
    const miniminStartDate = moment("2025-09-02"); // After initial migration
    const startDate = moment().subtract(1, "year");

    let startDateString
    if (startDate.isBefore(miniminStartDate)) {
      startDateString = miniminStartDate.format("YYYY-MM-DD");
    } else {
      startDateString = startDate.format("YYYY-MM-DD");
    }

    formRef.current?.resetFormValue({
      start: startDateString,
      end: endDate
    })
    loadClients()
  }, [])

  const loadClients = async () => {
    const clientes = await ClientApi.optionsList();
    setClients(clientes)
  }

  const getFormConfig = () => {
    return SalesByProductFormConfig;
  }

  const generateReport = async (formData: SalesByProductFormDataType) => {
    if (!product?.productId) {
      showAlert("danger", "Por favor seleccion un producto para generar el reporte de ventas")
      return
    }

    try {
      setIsLoading(true)

      const selectionSet = [
        "id",
        "cant",
        "price",
        "subtotal",
        "descuento",
        "impuestos",
        "exento",
        "codigo",
        "createdAt",
        "updatedAt",
        "invoice.id",
        "invoice.numero",
        "invoice.clientId",
        "invoice.fecha",
      ]

      const { end, start } = formData
      const startDateRange = moment(start).utc().toISOString()
      const endDateRange = moment(end).add(1, "day").utc().toISOString()

      let products: InvoiceProductDataType[] = []
      let nt = undefined;
      do {
        const { items, nextToken } = await InvoiceProductApi.fetchAllByProductId(product?.productId, selectionSet, startDateRange, endDateRange)
        nt = nextToken
        // Las lineas cuya factura ya no existe se descartan: no son ventas y
        // rompen el render del reporte al leer invoice.fecha.
        products = [
          ...products,
          ...items.filter(item => !!item.invoice)
        ]
      } while (nt)

      setProducts(products)
      generateGraphData(products)
      setGraphGenerated(true)
    } finally {
      setIsLoading(false)
    }
  }

  const generateGraphData = (products: InvoiceProductDataType[]) => {
    const graphData = groupSalesByMonth(products);
    setGraphData(graphData)
  }

  const groupSalesByMonth = (
    products: InvoiceProductDataType[] = [],
  ): SalesByMonthRow[] => {
    const acc = new Map<string, { totalCant: number; count: number }>();

    for (const it of products) {
      const createdAt = it?.createdAt;
      if (!createdAt) continue;

      const d = new Date(createdAt);
      if (isNaN(d.getTime())) continue;

      const y = d.getUTCFullYear();
      const m = d.getUTCMonth() + 1; // 0-11 -> 1-12
      const monthKey = `${y}-${String(m).padStart(2, "0")}`;

      const cant = Number(it?.cant) || 0;

      const prev = acc.get(monthKey) ?? { totalCant: 0, count: 0 };
      prev.totalCant += cant;
      prev.count += 1;
      acc.set(monthKey, prev);
    }

    // A arreglo y ordenar por mes ascendente
    return Array.from(acc.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([month, { totalCant, count }]) => ({
        month,
        totalCant: roundCant(totalCant),
        count,
      }));
  }

  const clickSearchProduct = () => {
    OpenAddProductPubSub.publish({ openOffCanvas: true })
  }

  const onProductSelect = (product: EDocumentProductDataType) => {
    setProduct(product);
    OpenAddProductPubSub.publish({ openOffCanvas: false })
  }

  const renderProductData = (): ReactNode => {
    return product?.productId && (
      <Row>
        <Col sm={6}>
          <Table className="">
            <tbody>
              <tr>
                <td className="col-2"><b>Codigo</b> </td>
                <td>{product.codigo}</td>
              </tr>
              <tr>
                <td> <b>Descripcion </b></td>
                <td>{product.description}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    )
  }

  const renderSaleProducts = (): ReactNode[] => {
    return products.map(p => {
      const fecha = moment(p.invoice.fecha);

      return (<tr key={p.id}>
        <td>
          <Link target="_blank" to={`/user/invoices/view/${p.invoice.id}`}>
            <b>{p.invoice.numero}</b>
          </Link>
        </td>
        <td>{fecha.format("DD-MM-YYYY")}</td>
        <td>{clients && clients[p.invoice.clientId] || ""}</td>
        <td>{formatCant(p.cant)}</td>
        <td>{toNumberFormat(`${p.price || ""}`)}</td>
      </tr>)
    })
  }

  return (
    <>
      <p>
        Por favor seleccione el producto al cual desea generarle el reporte de historial de ventas
      </p>
      <Button className="mb-3" color="success" type="button" onClick={clickSearchProduct}>
        Seleccionar Producto
      </Button>
      {renderProductData()}
      <DynamicForm<SalesByProductFormDataType>
        ref={formRef}
        id="report"
        formConfig={getFormConfig()}
        onSubmit={generateReport}
      >
        <Row>
          <Col md={{
            offset: 2
          }}>
            <Button className="me-3" type="submit" color="primary" size="lg">Generar Report</Button>
          </Col>
        </Row>
      </DynamicForm>

      {products.length > 0 && (
        <div>
          {graphData && (
            <BarGraph
              data={graphData}
              indexKey="month"
              valueKey="totalCant"
              xLabel="Mes"
              yLabel="Unidades"
            />
          )}

          <Table>
            <thead>
              <tr>
                <th># Factura</th>
                <th>Fecha</th>
                <th>Comprador</th>
                <th>Cantidad</th>
                <th>Precio Unitario</th>
              </tr>
            </thead>
            <tbody>
              {renderSaleProducts()}
            </tbody>
          </Table>
        </div>
      )}
      {isGraphGenerated && products.length == 0 ? "No hay datos disponibles para crear el report" : ""}

      <AddProductToDocumentOffCanvas
        hideTemporal={true}
        hideTemplate={true}
        productSelection={true}
        defaultSearchType="general"
        onProductSelect={onProductSelect}
      >
      </AddProductToDocumentOffCanvas>

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}
);

export default SalesByProductForm;